import { tracking } from 'App/Tracking';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Button } from '@unmind/design-system';
import RoutePath from 'App/RoutePath';
import { useTranslation } from 'react-i18next';
import { AuthTypeEnum } from '__generated__/globalTypes';
import getSubdomainFromUrl, {
  isClientSubdomain,
} from 'utils/getSubdomainFromUrl';
import { getChannel, getPrivacyPolicyLink } from 'utils';
import {
  LoggedOutScreenWrapperFooter,
  HelpIcon,
  HelpLinks,
  LinksContainer,
  LinkText,
  LockIcon,
} from './LoggedOutScreenWrapper.styled';

export const LoggedOutScreenFooter = ({
  customFooter,
  authType,
}: {
  customFooter?: React.ReactNode;
  authType?: AuthTypeEnum;
}) => {
  const { t: translate } = useTranslation('logged_out');
  const location = useLocation();
  const { supportUrl } = getChannel();
  const privacyUrl = getPrivacyPolicyLink();

  const hiddenCreateAccountPaths = [
    RoutePath.SignUp,
    RoutePath.SignInToYourOrganisation,
    RoutePath.InvalidInviteToken,
    RoutePath.InviteesLimitReached,
    RoutePath.AdditionalInformationNeeded,
    RoutePath.VerifyAccountOwnership,
    RoutePath.LoginWithSSO,
    RoutePath.FindYourOrganisationConfirmation,
    RoutePath.ResetPassword,
    RoutePath.ForgotPasswordEmailConfirmation,
    RoutePath.AccountVerification,
    RoutePath.EmailConfirmation,
    RoutePath.Register,
  ];

  const hideCreateAccountButton = hiddenCreateAccountPaths.some(path =>
    location.pathname.startsWith(path),
  );

  const subdomain = getSubdomainFromUrl();
  const clientSubdomain = isClientSubdomain(subdomain);
  const history = useHistory();

  const getSignUpRoute = (): string => {
    if (!clientSubdomain) {
      return `${RoutePath.SignInToYourOrganisation}?redirect=/signup`;
    }
    if (authType === AuthTypeEnum.AUTH0_SSO) {
      return RoutePath.LoginWithSSO;
    }

    return RoutePath.AccountVerification;
  };

  return (
    <>
      {customFooter || (
        <LoggedOutScreenWrapperFooter>
          {!hideCreateAccountButton && (
            <Button
              label={translate('footer.create_account_button.label')}
              onClick={() => {
                history.push(getSignUpRoute());
                tracking.track('create-account-clicked', { subdomain });
              }}
            />
          )}
          <LinksContainer>
            <HelpLinks href={supportUrl} data-testid="help-link">
              <HelpIcon />
              <LinkText>{translate('footer.help.label')}</LinkText>
            </HelpLinks>
            <HelpLinks href={privacyUrl} data-testid="privacy-link">
              <LockIcon />
              <LinkText>{translate('footer.privacy_policy.label')}</LinkText>
            </HelpLinks>
          </LinksContainer>
        </LoggedOutScreenWrapperFooter>
      )}
    </>
  );
};
